import ReactECharts from 'echarts-for-react';
import { backendURL } from "../Globals";
import React, { useState, useEffect, useMemo } from 'react';
import { Card, Col, Row, Statistic, Tabs, Spin, Slider } from 'antd';

/* ─── OA type palette ─────────────────────────────────────── */
const OA_COLORS = {
  'Diamond OA': '#722ed1',
  'Gold OA':    '#f0b429',
  'Hybrid OA':  '#4a90d9',
  'Bronze OA':  '#cd7f32',
  'Green OA':   '#52c41a',
  'Subscription': '#8c8c8c',
  'Other OA':   '#9966cc',
};

/* ─── Años completos (sin parciales) ─────────────────────── */
const COMPLETE_YEARS = ['2021', '2022', '2023', '2024', '2025'];
const PERIOD = COMPLETE_YEARS[0] + '–' + COMPLETE_YEARS[COMPLETE_YEARS.length - 1];

/* ─── Series apiladas del gráfico WOS anual ──────────────── */
const WOS_OA_LABELS = ['Diamond OA', 'Gold OA', 'Hybrid OA', 'Bronze OA', 'Green OA', 'Suscripción'];
const WOS_OA_COLORS = ['#722ed1', '#f0b429', '#4a90d9', '#cd7f32', '#52c41a', '#8c8c8c'];
const WOS_OA_KEYS   = ['diamondOA', 'goldOA', 'hybridOA', 'bronzeOA', 'greenOA', 'subscription'];

/* ─── Editores con acuerdo transformativo UniOvi ─────────── */
const AGREEMENT_PUBLISHERS = new Set([
  'Elsevier', 'Springer Nature', 'Wiley', 'IEEE',
  'AMER CHEMICAL SOC',          // ACS
  'ROYAL SOC CHEMISTRY',        // RSC
  'Cambridge University Press', // CUP
]);

/* ─── Editores vía Diamante (sin APC para autores) ───────── */
const DIAMOND_PUBLISHERS = new Set([
  'UNIV OVIEDO', 'UNIV OVIEDO, INST CIENCIAS EDUCACION',
  'UNIV COMPLUTENSE MADRID, SERVICIO PUBLICACIONES',
  'CONSEJO SUPERIOR INVESTIGACIONES CIENTIFICAS-CSIC',
  'UNIV MURCIA', 'EDICIONES UNIV SALAMANCA',
  'UNIV GRANADA, EDITORIAL', 'UNIV GRANADA',
  'UNIV NACIONAL EDUCACION DISTANCIA',
  'COLEGIO OFICIAL DE PSICOLOGOS DE ASTURIAS',
  'COLEGIO OFICIAL PSICOLOGOS MADRID',
  'CONSEJO GENERAL PSICOLOGIA DE ESPANA',
  'UNIV ALICANTE', 'UNIV SEVILLA, EDITORIAL',
  'UNIV POLITECNICA VALENCIA, EDITORIAL UPV',
  'ASOC PROFESORES FRANCES UNIV ESPANOLA-APFUE',
  'CENTRO ESTUDIOS POLITICOS CONSTITUCIONALES',
  'FEDERACION ESPANOLA ASOC DOCENTES EDUCACION FISICA-FEADEF',
  'UNIV SANTIAGO COMPOSTELA', 'SOC ESPANOLA MUSICOLOGIA',
  'EDICIONES UNIV VALLADOLID', 'UNIV VALLADOLID, SECRETARIADO PUBLICACIONES',
  'UNIV AUTONOMA BARCELONA', 'UNIV NAVARRA, SERVICIO PUBLICACIONES',
  'UNIV ALMERIA', 'UNIV BARCELONA, SERVEI PUBLICACIONS',
  'UNIV CASTILLA-LA MANCHA', 'UNIV CADIZ, SERV PUBLICACIONES',
  'UNIV CARLOS III MADRID, FAC HUMANIDADES COMUNICACION & DOCUMENTACION',
  'UNIV LAS PALMAS GRAN CANARIA, FAC GEOGRAFIA & HISTORIA',
  'UNIV NACL EDUCACION DISTANCIA-UNED, FAC EDUCACION',
  'UNIV NACL EDUCACION DISTANCIA-UNED, FUNDACION INST HISTORIA SOCIAL',
  'UNIV INT RIOJA-UNIR',
  'PONTIFICIA UNIV CATOLICA CHILE', 'MASARYK UNIV, FAC ARTS',
  'PRESSES UNIV BORDEAUX',
  'SOC GEOLOGICA ESPANA',
  'ASOC ESPANOLA PROFESORES DERECHO INT & RELACIONES INT-AEPDIRI',
  'ASOC UNIV FORMACION PROFESORADO-AUFOP',
  'FEDERACION ASOCIACIONES INGENIEROS INDUSTRIALES ESPANA',
  'SPANISH ASSOC IRISH STUDIES', 'ASSOC CULTURAL IMPOSSIBILIA',
  'SOCIDROGALCOHOL', 'SEAPA', 'SOC UNIV INVESTIGACION PSICOLOGIA & SALUD',
  'MINISTERIO DE SANIDAD Y CONSUMO',
  'CENTRO ESPANOL DOCUMENTACION DISCAPACIDAD-CEDD',
  'ASOCIACION ESPANOLES DE GEOGRAFIA',
  'INT GROUP OPERATIONAL MODAL ANALYSIS',
  'JMLR-JOURNAL MACHINE LEARNING RESEARCH',
  'IUSTEL', 'EIKASIA EDICIONES S L', 'INSULA LIBRERIA EDIC PUBL SA',
  'NOTYREG HISPANIA', 'ADAYA PRESS',
]);

/* ─── Nombres normalizados de editores ───────────────────── */
const PUBLISHER_NAME_MAP = {
  'UNIV OVIEDO': 'Universidad de Oviedo',
  'UNIV OVIEDO, INST CIENCIAS EDUCACION': 'ICE Univ. de Oviedo',
  'AMER CHEMICAL SOC': 'ACS',
  'ROYAL SOC CHEMISTRY': 'RSC',
  'EMERALD GROUP PUBLISHING LTD': 'Emerald',
  'CONSEJO SUPERIOR INVESTIGACIONES CIENTIFICAS-CSIC': 'CSIC',
  'UNIV MURCIA': 'Universidad de Murcia',
  'IUSTEL': 'Iustel',
  'EIKASIA EDICIONES S L': 'Eikasia',
  'EDICIONES UNIV SALAMANCA': 'Universidad de Salamanca',
  'UNIV GRANADA, EDITORIAL': 'Universidad de Granada',
  'UNIV GRANADA': 'Universidad de Granada',
  'UNIV NACIONAL EDUCACION DISTANCIA': 'UNED',
  'UNIV NACL EDUCACION DISTANCIA-UNED, FAC EDUCACION': 'UNED',
  'UNIV NACL EDUCACION DISTANCIA-UNED, FUNDACION INST HISTORIA SOCIAL': 'UNED',
  'COLEGIO OFICIAL DE PSICOLOGOS DE ASTURIAS': 'Colegio Psicólogos Asturias',
  'COLEGIO OFICIAL PSICOLOGOS MADRID': 'Colegio Psicólogos Madrid',
  'UNIV COMPLUTENSE MADRID, SERVICIO PUBLICACIONES': 'Universidad Complutense',
  'UNIV ALICANTE': 'Universidad de Alicante',
  'UNIV SEVILLA, EDITORIAL': 'Universidad de Sevilla',
  'UNIV POLITECNICA VALENCIA, EDITORIAL UPV': 'UPV',
  'ASOC PROFESORES FRANCES UNIV ESPANOLA-APFUE': 'APFUE',
  'CENTRO ESTUDIOS POLITICOS CONSTITUCIONALES': 'Centro Est. Políticos',
  'FEDERACION ESPANOLA ASOC DOCENTES EDUCACION FISICA-FEADEF': 'FEADEF',
  'UNIV SANTIAGO COMPOSTELA': 'Univ. de Santiago',
  'SOC ESPANOLA MUSICOLOGIA': 'Soc. Española de Musicología',
  'EDICIONES UNIV VALLADOLID': 'Universidad de Valladolid',
  'UNIV VALLADOLID, SECRETARIADO PUBLICACIONES': 'Universidad de Valladolid',
  'UNIV AUTONOMA BARCELONA': 'UAB',
  'UNIV NAVARRA, SERVICIO PUBLICACIONES': 'Universidad de Navarra',
  'LIPPINCOTT WILLIAMS & WILKINS': 'Lippincott',
  'UNIV ALMERIA': 'Univ. de Almería',
  'INT GROUP OPERATIONAL MODAL ANALYSIS': 'IOMAC',
  'INSULA LIBRERIA EDIC PUBL SA': 'Ínsula',
  'PRESSES UNIV BORDEAUX': 'Univ. Bordeaux',
  'JOHN BENJAMINS PUBLISHING CO': 'John Benjamins',
  'SOC GEOLOGICA ESPANA': 'Soc. Geológica de España',
  'PUBLIC LIBRARY SCIENCE': 'PLoS',
  'ASOC ESPANOLA PROFESORES DERECHO INT & RELACIONES INT-AEPDIRI': 'AEPDIRI',
  'UNIV CARLOS III MADRID, FAC HUMANIDADES COMUNICACION & DOCUMENTACION': 'Univ. Carlos III',
  'IOS PRESS': 'IOS Press',
  'NOTYREG HISPANIA': 'Notyreg',
  'LIVERPOOL UNIV PRESS': 'Liverpool Univ. Press',
  'ASSOC COMPUTING MACHINERY': 'ACM',
  'WORLD SCIENTIFIC PUBL CO PTE LTD': 'World Scientific',
  'AMER INST MATHEMATICAL SCIENCES-AIMS': 'AIMS',
  'JMLR-JOURNAL MACHINE LEARNING RESEARCH': 'JMLR',
  'SEAPA': 'SEAPA',
  'FEDERACION ASOCIACIONES INGENIEROS INDUSTRIALES ESPANA': 'Fed. Ing. Industriales',
  'INT UNION CRYSTALLOGRAPHY': 'IUCr',
  'GEORG THIEME VERLAG KG': 'Thieme',
  'MASARYK UNIV, FAC ARTS': 'Univ. Masaryk',
  'AMER PSYCHOLOGICAL ASSOC': 'APA',
  'UNIV LAS PALMAS GRAN CANARIA, FAC GEOGRAFIA & HISTORIA': 'ULPGC',
  'SOCIDROGALCOHOL': 'Socidrogalcohol',
  'DE GRUYTER POLAND SP Z O O': 'De Gruyter',
  'WALTER DE GRUYTER GMBH': 'De Gruyter',
  'UNIV BARCELONA, SERVEI PUBLICACIONS': 'Univ. de Barcelona',
  'BMJ PUBLISHING GROUP': 'BMJ',
  'UNIV CASTILLA-LA MANCHA': 'Univ. Castilla-La Mancha',
  'PONTIFICIA UNIV CATOLICA CHILE': 'PUC Chile',
  'SPANISH ASSOC IRISH STUDIES': 'Spanish Assoc. Irish Studies',
  'MINISTERIO DE SANIDAD Y CONSUMO': 'Ministerio de Sanidad',
  'SOC UNIV INVESTIGACION PSICOLOGIA & SALUD': 'Soc. Psicología y Salud',
  'PALGRAVE MACMILLAN LTD': 'Palgrave Macmillan',
  'ASSOC CULTURAL IMPOSSIBILIA': 'Impossibilia',
  'UNIV CADIZ, SERV PUBLICACIONES': 'Univ. de Cádiz',
  'ASOC UNIV FORMACION PROFESORADO-AUFOP': 'AUFOP',
  'UNIV INT RIOJA-UNIR': 'UNIR',
  'DOVE MEDICAL PRESS LTD': 'Dove Medical Press',
  'COPERNICUS GESELLSCHAFT MBH': 'Copernicus',
  'CENTRO ESPANOL DOCUMENTACION DISCAPACIDAD-CEDD': 'CEDD',
  'ASOCIACION ESPANOLES DE GEOGRAFIA': 'Asoc. Española de Geografía',
  'ROYAL SOC': 'Royal Society',
  'PEERJ INC': 'PeerJ',
  'CHURCHILL LIVINGSTONE': 'Churchill Livingstone',
  'EDICIONES DOYMA S A': 'Ediciones Doyma',
  'ADAYA PRESS': 'Adaya Press',
  'SISSA Medialab': 'SISSA Medialab',
  'IOP Publishing Ltd': 'IOP Publishing',
  'EDP SCIENCES S A': 'EDP Sciences',
  'AIP Publishing': 'AIP Publishing',
  'CONSEJO GENERAL PSICOLOGIA DE ESPANA': 'Consejo General Psicología',
  'INDERSCIENCE ENTERPRISES LTD': 'Inderscience',
  'ROUTLEDGE': 'Routledge',
  'CELL PRESS': 'Cell Press',
  'KARGER': 'Karger',
  'MDPI': 'MDPI',
  'AMER PHYSICAL SOC': 'APS',
  'BMC': 'BMC',
};
const normPub = name => PUBLISHER_NAME_MAP[name] ||
  (name.charAt(0).toUpperCase() + name.slice(1).toLowerCase());

/* ─── Nombres normalizados para acuerdos transformativos ─── */
const DASH_PUB_NAME_MAP = {
  'Cambridge University Press': 'CUP',
  'Cambridge UP': 'CUP',
};
const normDashPub = name => DASH_PUB_NAME_MAP[name] || name;

/* ─── Shared styles ───────────────────────────────────────── */
const cardStyle = {
  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
  borderRadius: '10px',
  marginBottom: '20px',
  height: '100%',
};
const cardTitleStyle = {
  fontSize: '18px',
  fontWeight: 'normal',
  color: '#555',
  textAlign: 'center',
  fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  letterSpacing: '0.5px',
};

/* ─── Helper: horizontal bar ──────────────────────────────── */
const fmtN = n => (n ?? 0).toLocaleString('de-DE');
const hBarOption = (labels, values, color = '#4a90d9') => ({
  tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' },
             formatter: p => `${p[0].name}: ${fmtN(p[0].value)}` },
  grid: { left: '2%', right: '10%', bottom: '3%', top: '3%', containLabel: true },
  xAxis: { type: 'value' },
  yAxis: { type: 'category', data: labels },
  series: [{
    type: 'bar',
    data: values,
    itemStyle: { color },
    label: { show: true, position: 'right', fontSize: 11, formatter: p => fmtN(p.value) },
  }],
});

/* ─── Helper: vertical bar ────────────────────────────────── */
const vBarOption = (labels, values, color = '#4a90d9') => ({
  tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' },
             formatter: p => `${p[0].name}: ${fmtN(p[0].value)}` },
  grid: { left: '3%', right: '4%', bottom: '30%', top: '8%', containLabel: true },
  xAxis: {
    type: 'category',
    data: labels,
    axisLabel: {
      interval: 0,
      rotate: 35,
      fontSize: 11,
      formatter: v => v.length > 20 ? v.slice(0, 20) + '…' : v,
    },
  },
  yAxis: { type: 'value' },
  series: [{
    type: 'bar',
    data: values,
    itemStyle: { color },
    label: { show: true, position: 'top', fontSize: 10, formatter: p => fmtN(p.value) },
  }],
});

/* ═══════════════════════════════════════════════════════════ */
const ReportsComponent = () => {

  /* ── Tab 1: WOS ─────────────────────────────────────────── */
  const [wosData,    setWosData]    = useState(null);
  const [wosLoading, setWosLoading] = useState(true);
  const [topN,          setTopN]          = useState(15);
  const [topNDiamond,   setTopNDiamond]   = useState(10);

  /* ── Tab 2: Acuerdos ────────────────────────────────────── */
  const [dashData,    setDashData]    = useState(null);
  const [dashLoading, setDashLoading] = useState(true);
  const [areasData,   setAreasData]   = useState([]);

  /* ── Datos de revistas elegibles (ocultos, sin borrar) ───── */
  const [jcrqData,          setJcrqData]          = useState([]);
  const [sjrqData,          setSjrqData]          = useState([]);
  const [publisherData,     setPublisherData]     = useState([]);
  const [businessModelData, setBusinessModelData] = useState([]);
  const [totalJournals,     setTotalJournals]     = useState('');

  /* ── Fetch ──────────────────────────────────────────────── */
  useEffect(() => {
    // Tab 1 – WOS JSON estático
    fetch('/data/wos.json')
      .then(r => r.json())
      .then(d => { setWosData(d); setWosLoading(false); })
      .catch(() => setWosLoading(false));

    // Tab 2 – dashboard JSON estático (desde Excel files)
    fetch('/data/dashboards.json')
      .then(r => r.json())
      .then(d => { setDashData(d); setDashLoading(false); })
      .catch(() => setDashLoading(false));

    // Tab 2 – publicaciones por área de conocimiento
    fetch(`${backendURL}/dashboards/areas`)
      .then(r => r.json())
      .then(setAreasData)
      .catch(() => {});

    // Datos de revistas (ocultos) – independientes
    const safeFetchJ = url => fetch(url).then(r => r.json()).catch(() => []);
    safeFetchJ(`${backendURL}/analytics/jcrq`).then(setJcrqData);
    safeFetchJ(`${backendURL}/analytics/sjrq`).then(setSjrqData);
    safeFetchJ(`${backendURL}/analytics/publisher`).then(setPublisherData);
    safeFetchJ(`${backendURL}/analytics/business-model`).then(setBusinessModelData);
    fetch(`${backendURL}/analytics/total-journals`)
      .then(r => r.json())
      .then(d => setTotalJournals(d[0]?.cantidad || ''))
      .catch(() => {});
  }, []);

  /* ── WOS: datos por año con vía Diamante ───────────────── */
  const wosByYear = useMemo(() => {
    if (!wosData) return {};
    const result = {};
    COMPLETE_YEARS.forEach(yr => {
      const diamondGold = wosData.publishers
        .filter(p => DIAMOND_PUBLISHERS.has(p.editorial))
        .reduce((s, p) => s + (p.byYear[yr]?.goldOA || 0), 0);
      const yrT = wosData.yearTotals[yr] || {};
      result[yr] = {
        diamondOA:    diamondGold,
        goldOA:       Math.max(0, (yrT.goldOA || 0) - diamondGold),
        hybridOA:     yrT.hybridOA || 0,
        bronzeOA:     yrT.bronzeOA || 0,
        greenOA:      yrT.greenOA || 0,
        subscription: yrT.subscription || 0,
      };
    });
    return result;
  }, [wosData]);

  const wosGlobal = useMemo(() => {
    const s = { diamondOA: 0, goldOA: 0, hybridOA: 0, bronzeOA: 0, greenOA: 0, subscription: 0 };
    COMPLETE_YEARS.forEach(yr => Object.keys(s).forEach(k => { s[k] += wosByYear[yr]?.[k] || 0; }));
    return s;
  }, [wosByYear]);

  const wosTotal   = useMemo(() => Object.values(wosGlobal).reduce((a, b) => a + b, 0), [wosGlobal]);
  const wosDiamond = useMemo(() => wosGlobal.diamondOA, [wosGlobal]);
  const wosOATotal = useMemo(() => wosTotal - wosGlobal.subscription, [wosTotal, wosGlobal]);
  const wosSub     = useMemo(() => wosGlobal.subscription, [wosGlobal]);

  const wosYearlyBar = useMemo(() => {
    if (!Object.keys(wosByYear).length) return {};
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' },
                 formatter: arr => arr.filter(p => p.value > 0).map(p => `${p.seriesName}: ${fmtN(p.value)}`).join('<br/>') },
      legend: { top: 8, data: WOS_OA_LABELS },
      grid: { left: '3%', right: '4%', bottom: '8%', top: '18%', containLabel: true },
      xAxis: { type: 'category', data: COMPLETE_YEARS },
      yAxis: { type: 'value' },
      series: WOS_OA_LABELS.map((label, i) => ({
        name: label,
        type: 'bar',
        stack: 'total',
        color: WOS_OA_COLORS[i],
        data: COMPLETE_YEARS.map(yr => wosByYear[yr]?.[WOS_OA_KEYS[i]] || 0),
      })),
    };
  }, [wosByYear]);

  const wosPublishersBar = useMemo(() => {
    if (!wosData) return {};
    const pubs = wosData.publishers.map(p => ({
      editorial: p.editorial,
      total: COMPLETE_YEARS.reduce((s, yr) => {
        const d = p.byYear[yr] || {};
        return s + (d.goldOA||0) + (d.hybridOA||0) + (d.bronzeOA||0) + (d.greenOA||0) + (d.subscription||0);
      }, 0),
    })).sort((a, b) => b.total - a.total);
    const top = pubs.slice(0, topN).reverse();
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { left: '2%', right: '12%', bottom: '3%', top: '3%', containLabel: true },
      xAxis: { type: 'value' },
      yAxis: { type: 'category', data: top.map(p => normPub(p.editorial)) },
      series: [{
        type: 'bar',
        data: top.map(p => ({
          value: p.total,
          itemStyle: { color: DIAMOND_PUBLISHERS.has(p.editorial) ? OA_COLORS['Diamond OA'] : '#4a90d9' },
        })),
        label: { show: true, position: 'right', fontSize: 11, formatter: p => fmtN(p.value) },
      }],
    };
  }, [wosData, topN]);

  const wosOADonut = useMemo(() => {
    if (!wosTotal) return {};
    return {
      tooltip: { trigger: 'item', formatter: p => `${p.name}: ${fmtN(p.value)} (${p.percent.toFixed(1)}%)` },
      legend: { orient: 'vertical', right: 4, top: 'center', textStyle: { fontSize: 11 } },
      series: [{
        type: 'pie',
        radius: ['38%', '60%'],
        center: ['36%', '50%'],
        avoidLabelOverlap: false,
        itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 },
        label: { show: false },
        emphasis: { label: { show: true, fontSize: 13, fontWeight: 'bold' } },
        labelLine: { show: false },
        data: [
          { value: wosDiamond,             name: 'Diamond OA',  itemStyle: { color: OA_COLORS['Diamond OA'] } },
          { value: wosGlobal.goldOA,       name: 'Gold OA',     itemStyle: { color: OA_COLORS['Gold OA'] } },
          { value: wosGlobal.hybridOA,     name: 'Hybrid OA',   itemStyle: { color: OA_COLORS['Hybrid OA'] } },
          { value: wosGlobal.bronzeOA,     name: 'Bronze OA',   itemStyle: { color: OA_COLORS['Bronze OA'] } },
          { value: wosGlobal.greenOA,      name: 'Green OA',    itemStyle: { color: OA_COLORS['Green OA'] } },
          { value: wosGlobal.subscription, name: 'Suscripción', itemStyle: { color: OA_COLORS['Subscription'] } },
        ].filter(d => d.value > 0),
      }],
    };
  }, [wosTotal, wosDiamond, wosGlobal]);

  /* ── Tab 4: OA Diamante ─────────────────────────────────── */
  const diamondPubs = useMemo(() => {
    if (!wosData) return [];
    return wosData.publishers
      .filter(p => DIAMOND_PUBLISHERS.has(p.editorial))
      .map(p => ({
        editorial: p.editorial,
        total: COMPLETE_YEARS.reduce((s, yr) => {
          const d = p.byYear[yr] || {};
          return s + (d.goldOA||0) + (d.hybridOA||0) + (d.bronzeOA||0) + (d.greenOA||0) + (d.subscription||0);
        }, 0),
      }))
      .filter(p => p.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [wosData]);

  const diamondTotal = useMemo(() => diamondPubs.reduce((s, p) => s + p.total, 0), [diamondPubs]);

  const diamondPublisherBar = useMemo(() => {
    if (!diamondPubs.length) return {};
    const top = diamondPubs.slice(0, topNDiamond).reverse();
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' },
                 formatter: p => `${p[0].name}: ${fmtN(p[0].value)}` },
      grid: { left: '2%', right: '12%', bottom: '3%', top: '3%', containLabel: true },
      xAxis: { type: 'value' },
      yAxis: { type: 'category', data: top.map(p => normPub(p.editorial)) },
      series: [{
        type: 'bar',
        color: OA_COLORS['Diamond OA'],
        data: top.map(p => p.total),
        label: { show: true, position: 'right', fontSize: 11, formatter: p => fmtN(p.value) },
      }],
    };
  }, [diamondPubs, topNDiamond]);

  const diamondYearBar = useMemo(() => {
    if (!wosData) return {};
    const yearData = {};
    COMPLETE_YEARS.forEach(yr => { yearData[yr] = 0; });
    wosData.publishers.forEach(p => {
      if (!DIAMOND_PUBLISHERS.has(p.editorial)) return;
      COMPLETE_YEARS.forEach(yr => {
        const d = p.byYear[yr] || {};
        yearData[yr] += (d.goldOA||0) + (d.hybridOA||0) + (d.bronzeOA||0) + (d.greenOA||0) + (d.subscription||0);
      });
    });
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' },
                 formatter: p => `${p[0].name}: ${fmtN(p[0].value)}` },
      grid: { left: '3%', right: '4%', bottom: '8%', top: '8%', containLabel: true },
      xAxis: { type: 'category', data: COMPLETE_YEARS },
      yAxis: { type: 'value' },
      series: [{
        type: 'bar',
        color: OA_COLORS['Diamond OA'],
        data: COMPLETE_YEARS.map(yr => yearData[yr]),
        label: { show: true, position: 'top', fontSize: 11, formatter: p => fmtN(p.value) },
      }],
    };
  }, [wosData]);

  /* ── Tab 2 chart options ────────────────────────────────── */
  const dashYearChart = useMemo(() => {
    if (!dashData) return {};
    return vBarOption(
      dashData.byYear.map(y => y.year),
      dashData.byYear.map(y => y.cantidad),
      '#52c41a',
    );
  }, [dashData]);

  const dashPublisherChart = useMemo(() => {
    if (!dashData) return {};
    return vBarOption(
      dashData.byPublisher.map(p => normDashPub(p.publisher)),
      dashData.byPublisher.map(p => p.cantidad),
      '#5b9bd5',
    );
  }, [dashData]);

  const dashJournalChart = useMemo(() => {
    if (!dashData) return {};
    const top = dashData.byJournal.slice(0, 15).reverse();
    return hBarOption(top.map(j => j.revista), top.map(j => j.cantidad), '#f0b429');
  }, [dashData]);

  const dashOADonut = useMemo(() => {
    if (!dashData) return {};
    const OA_DASH_COLORS = { 'Hybrid OA': '#4a90d9', 'Full OA': '#f0b429' };
    return {
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      legend: { top: '5%', left: 'center' },
      series: [{
        type: 'pie',
        radius: ['40%', '70%'],
        avoidLabelOverlap: false,
        itemStyle: { borderRadius: 8, borderColor: '#fff', borderWidth: 2 },
        label: { show: false },
        emphasis: { label: { show: true, fontSize: 16, fontWeight: 'bold' } },
        labelLine: { show: false },
        data: dashData.byOAType.map(o => ({
          value: o.cantidad,
          name: o.tipo,
          itemStyle: { color: OA_DASH_COLORS[o.tipo] || '#ccc' },
        })),
      }],
    };
  }, [dashData]);

  const dashHybridTotal = useMemo(() => dashData?.byOAType.find(o => o.tipo === 'Hybrid OA')?.cantidad || 0, [dashData]);
  const dashFullOATotal = useMemo(() => dashData?.byOAType.find(o => o.tipo === 'Full OA')?.cantidad   || 0, [dashData]);

  /* ── Tab 3: OA sin acuerdo ──────────────────────────────── */
  const noAgPubs = useMemo(() => {
    if (!wosData) return [];
    return wosData.publishers
      .filter(p => !AGREEMENT_PUBLISHERS.has(p.editorial) && !DIAMOND_PUBLISHERS.has(p.editorial))
      .map(p => ({
        ...p,
        goldPaid: COMPLETE_YEARS.reduce((s, yr) => s + (p.byYear[yr]?.goldOA  || 0), 0),
        hybPaid:  COMPLETE_YEARS.reduce((s, yr) => s + (p.byYear[yr]?.hybridOA || 0), 0),
        oaPaid:   COMPLETE_YEARS.reduce((s, yr) => s + (p.byYear[yr]?.goldOA  || 0) + (p.byYear[yr]?.hybridOA || 0), 0),
      }))
      .filter(p => p.oaPaid > 0)
      .sort((a, b) => b.oaPaid - a.oaPaid);
  }, [wosData]);

  const noAgTotal     = useMemo(() => noAgPubs.reduce((s, p) => s + p.oaPaid, 0), [noAgPubs]);
  const noAgGoldTotal = useMemo(() => noAgPubs.reduce((s, p) => s + p.goldPaid, 0), [noAgPubs]);
  const noAgHybTotal  = useMemo(() => noAgPubs.reduce((s, p) => s + p.hybPaid, 0), [noAgPubs]);

  const noAgPublisherBar = useMemo(() => {
    if (!noAgPubs.length) return {};
    const top = noAgPubs.slice(0, 15).reverse();
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      legend: { top: 4, data: ['Gold OA', 'Hybrid OA'] },
      grid: { left: '2%', right: '12%', bottom: '3%', top: '12%', containLabel: true },
      xAxis: { type: 'value' },
      yAxis: { type: 'category', data: top.map(p => normPub(p.editorial)) },
      series: [
        {
          name: 'Gold OA',
          type: 'bar',
          stack: 'oa',
          color: OA_COLORS['Gold OA'],
          data: top.map(p => p.goldPaid || 0),
          label: { show: false },
        },
        {
          name: 'Hybrid OA',
          type: 'bar',
          stack: 'oa',
          color: OA_COLORS['Hybrid OA'],
          data: top.map(p => p.hybPaid || 0),
          label: { show: false },
        },
        {
          type: 'bar',
          stack: 'oa',
          data: top.map(() => 0),
          itemStyle: { opacity: 0 },
          label: {
            show: true,
            position: 'right',
            fontSize: 11,
            formatter: p => fmtN(top[p.dataIndex].oaPaid),
          },
        },
      ],
    };
  }, [noAgPubs]);

  const noAgYearBar = useMemo(() => {
    if (!wosData || !noAgPubs.length) return {};
    const pubSet = new Set(noAgPubs.map(p => p.editorial));
    const yearGold = {};
    const yearHybrid = {};
    COMPLETE_YEARS.forEach(yr => { yearGold[yr] = 0; yearHybrid[yr] = 0; });
    wosData.publishers.forEach(p => {
      if (!pubSet.has(p.editorial)) return;
      COMPLETE_YEARS.forEach(yr => {
        yearGold[yr]   += p.byYear[yr]?.goldOA   || 0;
        yearHybrid[yr] += p.byYear[yr]?.hybridOA || 0;
      });
    });
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' },
                 formatter: arr => arr.filter(p => p.value > 0).map(p => `${p.seriesName}: ${fmtN(p.value)}`).join('<br/>') },
      legend: { top: 8, data: ['Gold OA', 'Hybrid OA'] },
      grid: { left: '3%', right: '4%', bottom: '8%', top: '18%', containLabel: true },
      xAxis: { type: 'category', data: COMPLETE_YEARS },
      yAxis: { type: 'value' },
      series: [
        { name: 'Gold OA',   type: 'bar', stack: 'yr', color: OA_COLORS['Gold OA'],   data: COMPLETE_YEARS.map(yr => yearGold[yr]) },
        { name: 'Hybrid OA', type: 'bar', stack: 'yr', color: OA_COLORS['Hybrid OA'], data: COMPLETE_YEARS.map(yr => yearHybrid[yr]) },
      ],
    };
  }, [wosData, noAgPubs]);

  const noAgDonut = useMemo(() => {
    if (!noAgTotal) return {};
    return {
      tooltip: { trigger: 'item', formatter: p => `${p.name}: ${fmtN(p.value)} (${p.percent.toFixed(1)}%)` },
      legend: { top: '5%', left: 'center' },
      series: [{
        type: 'pie',
        radius: ['40%', '70%'],
        avoidLabelOverlap: false,
        itemStyle: { borderRadius: 8, borderColor: '#fff', borderWidth: 2 },
        label: { show: false },
        emphasis: { label: { show: true, fontSize: 16, fontWeight: 'bold' } },
        labelLine: { show: false },
        data: [
          { value: noAgGoldTotal, name: 'Gold OA',   itemStyle: { color: OA_COLORS['Gold OA'] } },
          { value: noAgHybTotal,  name: 'Hybrid OA', itemStyle: { color: OA_COLORS['Hybrid OA'] } },
        ].filter(d => d.value > 0),
      }],
    };
  }, [noAgGoldTotal, noAgHybTotal, noAgTotal]);

  /* ── Áreas de conocimiento (Tab 2) ──────────────────────── */
  const AREA_PUB_COLORS = {
    'Elsevier': '#e87722', 'Springer': '#cc0000', 'Wiley': '#003087',
    'ACS': '#00843d', 'IEEE': '#00629b', 'Cambridge UP': '#7b1fa2',
  };
  const AREA_DEF_COLORS = ['#607d8b','#795548','#009688','#ff5722','#9e9e9e'];

  const areasChartOption = useMemo(() => {
    if (!areasData.length) return {};
    const top = areasData.slice(0, 20);
    const allPubs = [...new Set(areasData.flatMap(d => d.por_editorial.map(p => p.publisher)))];
    const categories = top.map(d => d.area_nombre).reverse();
    const series = allPubs.map((pub, i) => ({
      name: pub,
      type: 'bar',
      stack: 'total',
      data: top.map(d => (d.por_editorial.find(p => p.publisher === pub)?.count || 0)).reverse(),
      itemStyle: { color: AREA_PUB_COLORS[pub] || AREA_DEF_COLORS[i % AREA_DEF_COLORS.length] },
    }));
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      legend: { data: allPubs, top: 4, textStyle: { fontSize: 12 } },
      grid: { left: 4, right: 20, bottom: 8, top: 36, containLabel: true },
      xAxis: { type: 'value', minInterval: 1 },
      yAxis: { type: 'category', data: categories, axisLabel: { fontSize: 11, color: '#444', width: 230, overflow: 'truncate' } },
      series,
    };
  }, [areasData]);

  /* ── Resumen integrado (panel sobre las pestañas) ──────── */
  // Partición limpia desde wos.json: agr + noAg + diamond = wosOATotal exactamente
  const summaryOA = useMemo(() => {
    if (!wosData || !wosOATotal) return { agr: 0, noAg: 0, diamond: 0 };
    const agr = wosData.publishers
      .filter(p => AGREEMENT_PUBLISHERS.has(p.editorial))
      .reduce((s, p) => s + COMPLETE_YEARS.reduce((sy, yr) => {
        const d = p.byYear[yr] || {};
        return sy + (d.goldOA||0) + (d.hybridOA||0) + (d.bronzeOA||0) + (d.greenOA||0);
      }, 0), 0);
    const diamond = wosData.publishers
      .filter(p => DIAMOND_PUBLISHERS.has(p.editorial))
      .reduce((s, p) => s + COMPLETE_YEARS.reduce((sy, yr) => {
        const d = p.byYear[yr] || {};
        return sy + (d.goldOA||0) + (d.hybridOA||0) + (d.bronzeOA||0) + (d.greenOA||0);
      }, 0), 0);
    const noAg = Math.max(0, wosOATotal - agr - diamond);
    return { agr, noAg, diamond };
  }, [wosData, wosOATotal]);

  const summaryDonut = useMemo(() => {
    if (!wosOATotal) return {};
    return {
      tooltip: { trigger: 'item', textStyle: { fontWeight: 'normal' },
                 formatter: p => `${p.name}: ${fmtN(p.value)} (${p.percent.toFixed(1)}%)` },
      series: [
        {
          // Anillo exterior: OA total vs Suscripción
          type: 'pie',
          silent: false,
          radius: ['71%', '83%'],
          center: ['50%', '50%'],
          avoidLabelOverlap: false,
          itemStyle: { borderColor: '#fff', borderWidth: 1 },
          label: { show: true, position: 'inside', fontSize: 10, color: '#555', fontWeight: 'bold',
                   formatter: p => p.percent >= 4 ? `${p.percent.toFixed(0)}%` : '' },
          emphasis: { label: { show: false }, scale: false },
          labelLine: { show: false },
          data: [
            { value: wosOATotal, name: 'Acceso Abierto', itemStyle: { color: '#b7eb8f' } },
            { value: wosSub,     name: 'Suscripción',    itemStyle: { color: '#d9d9d9' } },
          ].filter(d => d.value > 0),
        },
        {
          // Donut principal
          type: 'pie',
          radius: ['42%', '70%'],
          center: ['50%', '50%'],
          avoidLabelOverlap: false,
          itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 },
          label: { show: false },
          emphasis: { label: { show: false }, scale: true, scaleSize: 6 },
          labelLine: { show: false },
          data: [
            { value: summaryOA.agr,     name: 'Acuerdo transform.', itemStyle: { color: '#52c41a' } },
            { value: summaryOA.noAg,    name: 'OA comercial',       itemStyle: { color: OA_COLORS['Gold OA'] } },
            { value: summaryOA.diamond, name: 'OA Diamante',        itemStyle: { color: OA_COLORS['Diamond OA'] } },
            { value: wosSub,            name: 'Suscripción',        itemStyle: { color: OA_COLORS['Subscription'] } },
          ].filter(d => d.value > 0),
        },
      ],
    };
  }, [summaryOA, wosSub, wosOATotal]);

  /* ── Tab 1 JSX ──────────────────────────────────────────── */
  const tab1 = (
    <Row gutter={[24, 24]} style={{ marginTop: 16 }}>

      {/* Stat cards only */}
      <Col span={24}>
        <Row gutter={[16, 16]} justify="center">
          <Col>
            <Card bordered={false} style={{ ...cardStyle, textAlign: 'center', minWidth: 190 }}>
              <Statistic
                title="Artículos UniOvi en WOS (CA)"
                value={wosLoading ? '…' : wosTotal}
                groupSeparator="."
              />
              <div style={{ color: '#aaa', fontSize: 12, marginTop: 4 }}>{PERIOD}</div>
            </Card>
          </Col>
          <Col>
            <Card bordered={false} style={{ ...cardStyle, textAlign: 'center', minWidth: 190 }}>
              <Statistic
                title="En Acceso Abierto"
                value={wosLoading ? '…' : wosOATotal}
                groupSeparator="."
                suffix={wosTotal ? ` (${Math.round(wosOATotal / wosTotal * 100)}%)` : ''}
              />
              <div style={{ color: '#aaa', fontSize: 12, marginTop: 4 }}>{PERIOD}</div>
            </Card>
          </Col>
          <Col>
            <Card bordered={false} style={{ ...cardStyle, textAlign: 'center', minWidth: 190 }}>
              <Statistic
                title="En Suscripción"
                value={wosLoading ? '…' : wosSub}
                groupSeparator="."
                suffix={wosTotal ? ` (${Math.round(wosSub / wosTotal * 100)}%)` : ''}
              />
              <div style={{ color: '#aaa', fontSize: 12, marginTop: 4 }}>{PERIOD}</div>
            </Card>
          </Col>
        </Row>
      </Col>

      {/* Yearly stacked bar */}
      <Col span={24}>
        <Card
          title={<span style={cardTitleStyle}>Publicaciones por año y tipo de acceso — CA UniOvi ({PERIOD})</span>}
          bordered={false} style={cardStyle}
        >
          {wosLoading
            ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
            : <ReactECharts option={wosYearlyBar} style={{ height: 340 }} />}
        </Card>
      </Col>

      {/* Publisher bar + topN slider */}
      <Col span={24}>
        <Card
          title={
            <Row justify="space-between" align="middle" style={{ flexWrap: 'nowrap' }}>
              <Col><span style={cardTitleStyle}>Editores — publicaciones CA UniOvi ({PERIOD})</span></Col>
              <Col style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 260 }}>
                <span style={{ fontSize: 13, color: '#888', whiteSpace: 'nowrap' }}>Top {topN}</span>
                <Slider
                  min={5}
                  max={wosData ? wosData.publishers.length : 100}
                  value={topN}
                  onChange={setTopN}
                  marks={{ 10: '10', 25: '25', 50: '50', 100: 'Todos' }}
                  style={{ flex: 1, minWidth: 160 }}
                />
              </Col>
            </Row>
          }
          bordered={false} style={cardStyle}
        >
          {wosLoading
            ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
            : <ReactECharts option={wosPublishersBar} style={{ height: Math.max(340, topN * 28) }} />}
        </Card>
      </Col>

    </Row>
  );

  /* ── Tab 2 JSX ──────────────────────────────────────────── */
  const tab2 = (
    <Row gutter={[24, 24]} style={{ marginTop: 16 }}>

      {/* Stat cards */}
      <Col span={24}>
        <Row gutter={[16, 16]} justify="center">
          <Col>
            <Card bordered={false} style={{ ...cardStyle, textAlign: 'center', minWidth: 200 }}>
              <Statistic title="Artículos en acuerdos transformativos" value={dashLoading ? '…' : dashData?.total ?? 0} groupSeparator="." />
            </Card>
          </Col>
          <Col>
            <Card bordered={false} style={{ ...cardStyle, textAlign: 'center', minWidth: 200 }}>
              <Statistic
                title="Hybrid OA"
                value={dashLoading ? '…' : dashHybridTotal}
                groupSeparator="."
                suffix={dashData?.total ? ` (${Math.round(dashHybridTotal / dashData.total * 100)}%)` : ''}
              />
            </Card>
          </Col>
          <Col>
            <Card bordered={false} style={{ ...cardStyle, textAlign: 'center', minWidth: 200 }}>
              <Statistic
                title="Full OA"
                value={dashLoading ? '…' : dashFullOATotal}
                groupSeparator="."
                suffix={dashData?.total ? ` (${Math.round(dashFullOATotal / dashData.total * 100)}%)` : ''}
              />
            </Card>
          </Col>
        </Row>
      </Col>

      {/* By year */}
      <Col span={24} md={12}>
        <Card title={<span style={cardTitleStyle}>Publicaciones por año</span>} bordered={false} style={cardStyle}>
          {dashLoading
            ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
            : <ReactECharts option={dashYearChart} style={{ height: 300 }} />}
        </Card>
      </Col>

      {/* By publisher */}
      <Col span={24} md={12}>
        <Card title={<span style={cardTitleStyle}>Publicaciones por editor</span>} bordered={false} style={cardStyle}>
          {dashLoading
            ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
            : <ReactECharts option={dashPublisherChart} style={{ height: 300 }} />}
        </Card>
      </Col>

      {/* Top journals */}
      <Col span={24} md={14}>
        <Card
          title={<span style={cardTitleStyle}>Top 15 revistas en acuerdos transformativos</span>}
          bordered={false} style={cardStyle}
        >
          {dashLoading
            ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
            : <ReactECharts option={dashJournalChart} style={{ height: 440 }} />}
        </Card>
      </Col>

      {/* OA type donut */}
      <Col span={24} md={10}>
        <Card
          title={<span style={cardTitleStyle}>Tipo de acceso (Hybrid vs Full OA)</span>}
          bordered={false} style={cardStyle}
        >
          {dashLoading
            ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
            : <ReactECharts option={dashOADonut} style={{ height: 440 }} />}
        </Card>
      </Col>

      {/* Publicaciones por área de conocimiento */}
      {areasData.length > 0 && (
        <Col span={24}>
          <Card
            title={<span style={cardTitleStyle}>Publicaciones en acuerdos transformativos por área de conocimiento (2021–2025)</span>}
            bordered={false} style={cardStyle}
          >
            <ReactECharts
              option={areasChartOption}
              style={{ height: Math.max(320, areasData.slice(0, 20).length * 28 + 60) }}
              opts={{ renderer: 'svg' }}
            />
            <div style={{ fontSize: 12, color: '#bbb', marginTop: 8 }}>
              Basado en el corresponding author registrado en cada acuerdo. Solo se contabilizan autores identificados en el PDI de la Universidad de Oviedo.
            </div>
          </Card>
        </Col>
      )}

    </Row>
  );

  /* ── Tab 3 JSX ──────────────────────────────────────────── */
  const tab3 = (
    <Row gutter={[24, 24]} style={{ marginTop: 16 }}>

      <Col span={24}>
        <Row gutter={[16, 16]} justify="center">
          <Col>
            <Card bordered={false} style={{ ...cardStyle, textAlign: 'center', minWidth: 200 }}>
              <Statistic title="Publicaciones OA sin acuerdo" value={wosLoading ? '…' : noAgTotal} groupSeparator="." />
            </Card>
          </Col>
          <Col>
            <Card bordered={false} style={{ ...cardStyle, textAlign: 'center', minWidth: 200 }}>
              <Statistic
                title="Gold OA (revista OA)"
                value={wosLoading ? '…' : noAgGoldTotal}
                groupSeparator="."
                suffix={noAgTotal ? ` (${Math.round(noAgGoldTotal / noAgTotal * 100)}%)` : ''}
              />
            </Card>
          </Col>
          <Col>
            <Card bordered={false} style={{ ...cardStyle, textAlign: 'center', minWidth: 200 }}>
              <Statistic
                title="Hybrid OA (APC en revista mixta)"
                value={wosLoading ? '…' : noAgHybTotal}
                groupSeparator="."
                suffix={noAgTotal ? ` (${Math.round(noAgHybTotal / noAgTotal * 100)}%)` : ''}
              />
            </Card>
          </Col>
        </Row>
      </Col>

      <Col span={24}>
        <Card
          title={<span style={cardTitleStyle}>Evolución por año — OA sin acuerdo transformativo (CA UniOvi)</span>}
          bordered={false} style={cardStyle}
        >
          {wosLoading
            ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
            : <ReactECharts option={noAgYearBar} style={{ height: 320 }} />}
        </Card>
      </Col>

      <Col span={24} md={14}>
        <Card
          title={<span style={cardTitleStyle}>Top 15 editores sin acuerdo — publicaciones OA pagadas</span>}
          bordered={false} style={cardStyle}
        >
          {wosLoading
            ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
            : <ReactECharts option={noAgPublisherBar} style={{ height: 440 }} />}
        </Card>
      </Col>

      <Col span={24} md={10}>
        <Card
          title={<span style={cardTitleStyle}>Gold OA vs Hybrid OA sin acuerdo</span>}
          bordered={false} style={cardStyle}
        >
          {wosLoading
            ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
            : <ReactECharts option={noAgDonut} style={{ height: 440 }} />}
        </Card>
      </Col>

    </Row>
  );

  /* ── Tab 4 JSX ──────────────────────────────────────────── */
  const tab4 = (
    <Row gutter={[24, 24]} style={{ marginTop: 16 }}>

      <Col span={24}>
        <Row gutter={[16, 16]} justify="center">
          <Col>
            <Card bordered={false} style={{ ...cardStyle, textAlign: 'center', minWidth: 220 }}>
              <Statistic
                title="Publicaciones OA Diamante"
                value={wosLoading ? '…' : diamondTotal}
                groupSeparator="."
                valueStyle={{ color: OA_COLORS['Diamond OA'] }}
              />
              <div style={{ color: '#aaa', fontSize: 12, marginTop: 4 }}>{PERIOD}</div>
            </Card>
          </Col>
        </Row>
      </Col>

      <Col span={24} md={10}>
        <Card
          title={<span style={cardTitleStyle}>Evolución anual — OA Diamante ({PERIOD})</span>}
          bordered={false} style={cardStyle}
        >
          {wosLoading
            ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
            : <ReactECharts option={diamondYearBar} style={{ height: 300 }} />}
        </Card>
      </Col>

      <Col span={24} md={14}>
        <Card
          title={
            <Row justify="space-between" align="middle" style={{ flexWrap: 'nowrap' }}>
              <Col><span style={cardTitleStyle}>Editores Diamond OA — publicaciones CA UniOvi ({PERIOD})</span></Col>
              <Col style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 220 }}>
                <span style={{ fontSize: 13, color: '#888', whiteSpace: 'nowrap' }}>Top {topNDiamond}</span>
                <Slider
                  min={5}
                  max={diamondPubs.length || 10}
                  value={topNDiamond}
                  onChange={setTopNDiamond}
                  marks={{ 10: '10', 20: '20', 30: '30' }}
                  style={{ flex: 1, minWidth: 140 }}
                />
              </Col>
            </Row>
          }
          bordered={false} style={cardStyle}
        >
          {wosLoading
            ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
            : <ReactECharts option={diamondPublisherBar} style={{ height: Math.max(300, topNDiamond * 28) }} />}
        </Card>
      </Col>

    </Row>
  );

  /* ── Render ─────────────────────────────────────────────── */
  return (
    <div>
      {/* Header — full width */}
      <div style={{ paddingTop: 28, paddingBottom: 4, textAlign: 'center', background: '#fafafa' }}>
        <div style={{
          fontSize: 22, fontWeight: 700, color: '#1a1a1a',
          letterSpacing: -0.3, lineHeight: 1.3,
          fontFamily: "'Inter','Segoe UI',sans-serif",
          padding: '0 24px',
        }}>
          Estadísticas
        </div>
        <div style={{ fontSize: 13, color: '#888', marginTop: 6, paddingBottom: 16 }}>
          Período de análisis {PERIOD}
        </div>
      </div>

      {/* Aviso fuentes de datos */}
      <div style={{
        margin: '16px 20px 0',
        padding: '12px 18px',
        background: '#fffbe6',
        border: '1px solid #ffe58f',
        borderRadius: 8,
        fontSize: 13,
        color: '#7c5c00',
        lineHeight: 1.7,
      }}>
        <strong>Nota sobre las fuentes de datos:</strong> Los datos ofrecidos tienen dos fuentes: publicaciones de la UniOvi indexadas en WoS (Clarivate) para el período 2021–2025, y artículos publicados a través de acuerdo transformativo para el período 2021–2025 extraídos de los <em>reports</em> de los <em>dashboards</em> de los editores. Esta información se actualiza anualmente.
      </div>

    <div style={{ padding: '20px' }}>

      {/* ── Panel resumen integrado ──────────────────────── */}
      <Card bordered={false} style={{ ...cardStyle, marginBottom: 8 }}>
        <Row justify="center" align="stretch" style={{ flexWrap: 'nowrap' }}>

          {/* Columna 1: Acceso Abierto + Suscripción */}
          <Col style={{ flex: '0 0 33%', maxWidth: 260, textAlign: 'center', padding: '20px 24px' }}>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 34, fontWeight: 700, color: '#333', lineHeight: 1 }}>
                {wosLoading ? '…' : fmtN(wosOATotal)}
              </div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                {(!wosLoading && wosTotal) ? `${Math.round(wosOATotal / wosTotal * 100)}%` : ''}
              </div>
              <div style={{ fontSize: 13, color: '#555', marginTop: 4 }}>En Acceso Abierto</div>
            </div>
            <div style={{ width: 36, height: 1, background: '#e8e8e8', margin: '0 auto 20px' }} />
            <div>
              <div style={{ fontSize: 28, fontWeight: 600, color: OA_COLORS['Subscription'], lineHeight: 1 }}>
                {wosLoading ? '…' : fmtN(wosSub)}
              </div>
              <div style={{ fontSize: 12, color: '#aaa', marginTop: 4 }}>
                {(!wosLoading && wosTotal) ? `${Math.round(wosSub / wosTotal * 100)}%` : ''}
              </div>
              <div style={{ fontSize: 13, color: '#555', marginTop: 4 }}>En Suscripción</div>
            </div>
          </Col>

          <Col style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ width: 1, height: 110, background: '#e8e8e8' }} />
          </Col>

          {/* Columna 2: Acuerdo transformativo + OA comercial */}
          <Col style={{ flex: '0 0 33%', maxWidth: 260, textAlign: 'center', padding: '20px 24px' }}>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 34, fontWeight: 700, color: '#52c41a', lineHeight: 1 }}>
                {wosLoading ? '…' : fmtN(summaryOA.agr)}
              </div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                {(!wosLoading && wosOATotal)
                  ? `${Math.round(summaryOA.agr / wosOATotal * 100)}%` : ''}
              </div>
              <div style={{ fontSize: 13, color: '#555', marginTop: 6 }}>A través de acuerdo transformativo</div>
            </div>
            <div style={{ width: 36, height: 1, background: '#e8e8e8', margin: '0 auto 20px' }} />
            <div>
              <div style={{ fontSize: 28, fontWeight: 600, color: OA_COLORS['Gold OA'], lineHeight: 1 }}>
                {wosLoading ? '…' : fmtN(noAgTotal)}
              </div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                {(!wosLoading && wosOATotal) ? `${Math.round(noAgTotal / wosOATotal * 100)}%` : ''}
              </div>
              <div style={{ fontSize: 13, color: '#555', marginTop: 4 }}>Por OA comercial sin acuerdo</div>
            </div>
          </Col>

          <Col style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ width: 1, height: 110, background: '#e8e8e8' }} />
          </Col>

          {/* Columna 3: OA Diamante + Donut */}
          <Col style={{ flex: '0 0 33%', maxWidth: 260, textAlign: 'center', padding: '20px 24px' }}>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 34, fontWeight: 700, color: OA_COLORS['Diamond OA'], lineHeight: 1 }}>
                {wosLoading ? '…' : fmtN(diamondTotal)}
              </div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                {(!wosLoading && wosOATotal) ? `${Math.round(diamondTotal / wosOATotal * 100)}%` : ''}
              </div>
              <div style={{ fontSize: 13, color: '#555', marginTop: 6 }}>OA Diamante</div>
            </div>
            {(wosLoading || dashLoading)
              ? <div style={{ padding: 20 }}><Spin /></div>
              : <ReactECharts option={summaryDonut} style={{ height: 130 }} />}
          </Col>

        </Row>
      </Card>

      <Tabs
        defaultActiveKey="1"
        size="large"
        centered
        items={[
          { key: '1', label: 'Publicaciones en WOS', children: tab1 },
          { key: '2', label: 'Acuerdos transformativos', children: tab2 },
          { key: '3', label: 'OA sin acuerdo transformativo', children: tab3 },
          { key: '4', label: 'OA Diamante', children: tab4 },
        ]}
      />
    </div>
    </div>
  );
};

export default ReportsComponent;

/*
 * ── Datos de revistas elegibles (ocultos, sin borrar) ─────────────────
 * Los states jcrqData, sjrqData, publisherData, businessModelData y
 * totalJournals siguen cargándose y están listos para re-activar
 * los gráficos de revistas en cualquier momento.
 * ──────────────────────────────────────────────────────────────────────
 */





